/**
 * Where a link inside a rendered `.spec/*.md` doc should go. The markdown is
 * written for GitHub, so its relative hrefs are relative to the file's place
 * in the repo — rendered as-is they resolve against the dashboard's own URL
 * (`./ARCH.md` on /repos/o/r becomes /repos/o/ARCH.md, a repo that doesn't
 * exist). So: a sibling spec doc opens in the panel (its href is still the
 * GitHub URL, for middle-click / open-in-new-tab), any other repo-relative
 * path opens on GitHub, absolute URLs open in a new tab, and `#anchor` /
 * `mailto:` etc. are left to the browser. A sibling doc link's `#fragment`
 * comes back decoded, for the panel to scroll to once that doc renders.
 */
export type SpecLink =
  | { kind: "spec"; file: string; href: string; fragment: string | null }
  | { kind: "external"; href: string }
  | { kind: "plain"; href: string };

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

export function resolveSpecLink(
  href: string,
  owner: string,
  name: string,
  files: readonly string[],
): SpecLink {
  if (href === "" || href.startsWith("#")) return { kind: "plain", href };
  if (SCHEME.test(href) || href.startsWith("//")) {
    return /^(https?:)?\/\//i.test(href)
      ? { kind: "external", href }
      : { kind: "plain", href };
  }

  const repoRoot = githubRoot(owner, name, "blob");
  const url = resolveInRepo(href, repoRoot);
  // `..` past the repo root climbs into github.com itself; point at the root.
  if (!url) return { kind: "external", href: repoRoot };

  const specPrefix = `${new URL(repoRoot).pathname}.spec/`;
  if (url.pathname.startsWith(specPrefix)) {
    let file: string | null = null;
    try {
      file = decodeURIComponent(url.pathname.slice(specPrefix.length));
    } catch {
      // Malformed `%` escape (`50%off.md`) — not a doc we can list; GitHub it.
    }
    if (file !== null && files.includes(file)) {
      return { kind: "spec", file, href: url.href, fragment: decodeFragment(url.hash) };
    }
  }
  return { kind: "external", href: url.href };
}

/**
 * Where an `<img>` inside a rendered spec doc should load from. Same problem
 * as links: `./diagram.png` would resolve against the dashboard's URL and
 * come back as the SPA's index.html. github.com's `/raw/` path only works for
 * public repos — a cross-site `<img>` doesn't carry GitHub's SameSite session
 * cookies — so repo-relative srcs go through the API's spec-asset proxy, which
 * reads the file with the stored token. Absolute and `data:` srcs are left
 * alone; a src that climbs out of the repo gets none rather than a guess.
 */
export function resolveSpecImage(
  src: string,
  owner: string,
  name: string,
): string | undefined {
  if (src === "" || src.startsWith("#") || SCHEME.test(src) || src.startsWith("//")) {
    return src;
  }
  const root = githubRoot(owner, name, "raw");
  const url = resolveInRepo(src, root);
  if (!url) return undefined;
  let path: string;
  try {
    path = decodeURIComponent(url.pathname.slice(new URL(root).pathname.length));
  } catch {
    return undefined; // malformed `%` escape — no file it could name
  }
  return `/api/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/spec-asset?path=${encodeURIComponent(path)}`;
}

function githubRoot(owner: string, name: string, view: "blob" | "raw"): string {
  return `https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/${view}/HEAD/`;
}

/** Resolve a doc-relative href inside `root`, or null if it climbs out. */
function resolveInRepo(href: string, root: string): URL | null {
  // GitHub reads a leading `/` as the repo root, not the host root.
  const url = href.startsWith("/")
    ? new URL(href.slice(1), root)
    : new URL(href, `${root}.spec/`);
  return url.href.startsWith(root) ? url : null;
}

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

/**
 * Heading ids, so a spec doc's `#anchor` links (its table of contents) have
 * something to land on. The slug is GitHub's — lowercase, keep letters,
 * digits, `_`, `-` and spaces, spaces to `-`, `-1`, `-2`… on a repeat — since
 * the anchors were written against github.com. Prefixed like GitHub's, so a
 * heading named "Root" can't take the app's own `#root`; `specAnchorId` maps
 * a link's fragment onto it. A heading that already has an id (GFM's
 * footnote label, which the refs' `aria-describedby` points at) keeps it.
 */
export function rehypeHeadingIds() {
  return (tree: HastNode) => {
    const seen = new Map<string, number>();
    const walk = (node: HastNode) => {
      if (
        node.type === "element" &&
        /^h[1-6]$/.test(node.tagName ?? "") &&
        node.properties?.id == null
      ) {
        const base = textOf(node)
          .toLowerCase()
          .replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, "")
          .replace(/ /g, "-");
        let slug = base;
        while (seen.has(slug)) {
          const n = (seen.get(base) ?? 0) + 1;
          seen.set(base, n);
          slug = `${base}-${n}`;
        }
        seen.set(slug, 0);
        node.properties = { ...node.properties, id: specAnchorId(slug) };
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

export function specAnchorId(fragment: string): string {
  return `user-content-${fragment}`;
}

/** `#a%20b` → `a b`; null for no fragment or a malformed `%` escape. */
export function decodeFragment(hash: string): string | null {
  if (hash.length <= 1) return null;
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }
}

function textOf(node: HastNode): string {
  return node.type === "text" ? (node.value ?? "") : (node.children ?? []).map(textOf).join("");
}
